<template>
  <div class="sq-tool">
    <!-- Mode selector -->
    <div class="sq-card">
      <div class="sq-card-hd">
        <span class="sq-dot sq-dot-blue" />
        <span>{{ $t('tool.SqlQuery.title') }}</span>
        <span class="sq-badge sq-badge-on" v-if="connected">{{ $t('tool.SqlQuery.connected') }} {{ conn.database }}</span>
        <el-radio-group v-model="mode" size="mini" class="sq-mode-switch" @change="onModeChange">
          <el-radio-button label="direct">{{ $t('tool.SqlQuery.directMode') }}</el-radio-button>
          <el-radio-button label="agent">{{ $t('tool.SqlQuery.agentMode') }}</el-radio-button>
        </el-radio-group>
      </div>

      <!-- Direct mode: connection form -->
      <div class="sq-form" v-if="mode==='direct'">
        <div class="sq-row">
          <div class="sq-field" style="flex:2"><label>{{ $t('tool.SqlQuery.host') }}</label><el-input v-model="conn.host" size="small" placeholder="127.0.0.1" /></div>
          <div class="sq-field"><label>{{ $t('tool.SqlQuery.port') }}</label><el-input v-model="conn.port" size="small" placeholder="3306" /></div>
          <div class="sq-field"><label>{{ $t('tool.SqlQuery.database') }}</label><el-input v-model="conn.database" size="small" placeholder="test" /></div>
        </div>
        <div class="sq-row">
          <div class="sq-field"><label>{{ $t('tool.SqlQuery.user') }}</label><el-input v-model="conn.user" size="small" placeholder="root" /></div>
          <div class="sq-field"><label>{{ $t('tool.SqlQuery.password') }}</label><el-input v-model="conn.password" size="small" type="password" show-password /></div>
        </div>
        <div>
          <el-button type="primary" size="small" @click="doConnect" :loading="connecting">{{ connected ? $t('tool.SqlQuery.reconnect') : $t('tool.SqlQuery.btnConnect') }}</el-button>
          <el-button size="small" @click="disconnect" v-if="connected">{{ $t('tool.SqlQuery.btnDisconnect') }}</el-button>
        </div>
      </div>

      <!-- Agent mode: session + db form -->
      <div class="sq-form" v-if="mode==='agent'">
        <div class="sq-agent-help">
          <i class="el-icon-info" /> {{ $t('tool.SqlQuery.agentHelp') }}
          <a :href="agentDownloadUrl" target="_blank" class="sq-agent-dl">{{ $t('tool.SqlQuery.downloadAgent') }}</a>
        </div>
        <div class="sq-row">
          <div class="sq-field"><label>{{ $t('tool.SqlQuery.sessionId') }}</label><el-input v-model="conn.sessionId" size="small" :placeholder="$t('tool.SqlQuery.phSessionId')" /></div>
        </div>
        <div class="sq-row">
          <div class="sq-field" style="flex:2"><label>{{ $t('tool.SqlQuery.host') }}</label><el-input v-model="conn.host" size="small" placeholder="127.0.0.1" /></div>
          <div class="sq-field"><label>{{ $t('tool.SqlQuery.port') }}</label><el-input v-model="conn.port" size="small" placeholder="3306" /></div>
          <div class="sq-field"><label>{{ $t('tool.SqlQuery.database') }}</label><el-input v-model="conn.database" size="small" placeholder="test" /></div>
        </div>
        <div class="sq-row">
          <div class="sq-field"><label>{{ $t('tool.SqlQuery.user') }}</label><el-input v-model="conn.user" size="small" placeholder="root" /></div>
          <div class="sq-field"><label>{{ $t('tool.SqlQuery.password') }}</label><el-input v-model="conn.password" size="small" type="password" show-password /></div>
        </div>
        <div>
          <el-button type="primary" size="small" @click="doConnect" :loading="connecting" :disabled="!conn.sessionId">
            {{ agentConnected ? $t('tool.SqlQuery.reconnect') : $t('tool.SqlQuery.btnConnect') }}
          </el-button>
          <el-button size="small" @click="disconnect" v-if="connected">{{ $t('tool.SqlQuery.btnDisconnect') }}</el-button>
        </div>
      </div>
    </div>

    <!-- SQL workspace (tables + editor + actions) -->
    <div class="sq-card" v-if="connected">
      <div class="sq-card-hd">
        <span class="sq-dot sq-dot-purple" />
        <span>{{ $t('tool.SqlQuery.editor') }}</span>
        <span class="sq-card-hint">{{ $t('tool.SqlQuery.readonlyHint') }}</span>
      </div>
      <div class="sq-tags" v-if="tables.length">
        <el-tag v-for="t in tables" :key="t" size="small" class="sq-tag" @click="selectTable(t)" :type="sql.includes(t)?'success':'info'">{{ t }}</el-tag>
      </div>
      <el-input v-model="sql" type="textarea" :rows="4" placeholder="SELECT * FROM users LIMIT 10" class="sq-editor" @keyup.ctrl.enter="execute" />
      <div class="sq-actions">
        <el-button type="primary" size="small" @click="execute" :loading="loading">{{ $t('tool.SqlQuery.btnRun') }}</el-button>
        <el-button size="small" @click="doAction('clear')">{{ $t("common.action.clear") }}</el-button>
        <el-button size="small" @click="doAction('copy')" :disabled="!result">{{ $t('tool.SqlQuery.copyResult') }}</el-button>
      </div>
    </div>

    <!-- Result -->
    <div class="sq-card" v-if="result">
      <div class="sq-card-hd">
        <span class="sq-dot sq-dot-green" />
        <span>{{ $t('tool.SqlQuery.result') }}</span>
        <span class="sq-card-hint">{{ result.msg }}</span>
      </div>
      <div v-if="result.columns" class="sq-table-wrap">
        <table class="sq-table">
          <thead><tr><th v-for="c in result.columns" :key="c">{{ c }}</th></tr></thead>
          <tbody><tr v-for="(row,i) in result.values" :key="i"><td v-for="(v,j) in row" :key="j">{{ v }}</td></tr></tbody>
        </table>
      </div>
      <div v-else class="sq-msg">{{ result.msg }}</div>
    </div>

    <!-- Error -->
    <div class="sq-card sq-card-err" v-if="error">
      <div class="sq-err">{{ error }}</div>
    </div>
  </div>
</template>

<script>
import request from '@/api/request'

export default {
  name:'SqlQueryTool',props:{config:{type:Object,default:()=>({})}},
  data:()=>({
    mode:'direct',
    conn:{host:'127.0.0.1',port:'3306',user:'root',password:'',database:'',sessionId:''},
    connected:false,connecting:false,
    agentConnected:false,
    sql:'',tables:[],result:null,error:'',loading:false,
    ws:null,
    heartbeatTimer:null,reconnectTimer:null,reconnectDelay:0,intentionalClose:false
  }),
  computed:{
    agentDownloadUrl(){
      return 'https://onltool.site/static/agent.exe'
    }
  },
  beforeDestroy(){
    this.closeWs()
  },
  methods:{
    onModeChange(){
      this.disconnect()
    },
    closeWs(){
      this.stopHeartbeat();this.clearReconnect()
      this.intentionalClose=true
      if(this.ws){ this.ws.close(); this.ws=null }
    },
    disconnect(){
      this.closeWs()
      this.connected=false; this.agentConnected=false
      this.tables=[]; this.result=null; this.error=''
      this.connecting=false; this.loading=false
    },
    startHeartbeat(){
      this.stopHeartbeat()
      this.heartbeatTimer=setInterval(()=>{
        if(this.ws&&this.ws.readyState===WebSocket.OPEN){
          this.ws.send(JSON.stringify({type:'ping'}))
        }
      },15000)
    },
    stopHeartbeat(){
      if(this.heartbeatTimer){clearInterval(this.heartbeatTimer);this.heartbeatTimer=null}
    },
    clearReconnect(){
      if(this.reconnectTimer){clearTimeout(this.reconnectTimer);this.reconnectTimer=null}
    },
    scheduleReconnect(){
      if(this.mode!=='agent'||this.intentionalClose)return
      if(this.reconnectDelay>=3){
        this.error=this.$t('tool.SqlQuery.reconnectFailed')
        this.connected=false; this.stopHeartbeat(); this.ws=null
        return
      }
      const delay=Math.min(1000*Math.pow(2,this.reconnectDelay),30000)
      this.reconnectDelay++
      this.reconnectTimer=setTimeout(()=>{
        this.error=this.$t('tool.SqlQuery.reconnecting')
        this.connectViaAgent()
      },delay)
    },
    async doConnect(){
      if(!this.conn.host||!this.conn.database)return
      if(this.mode==='agent'&&!this.conn.sessionId)return
      this.connecting=true;this.error='';this.tables=[];this.result=null

      if(this.mode==='agent'){
        this.connectViaAgent()
      }else{
        this.connectDirect()
      }
    },
    async connectDirect(){
      this.closeWs()
      try{
        const res=await request({url:'/api/pub/sql/query',method:'post',data:{
          host:this.conn.host,port:Number(this.conn.port)||3306,
          user:this.conn.user,password:this.conn.password,
          database:this.conn.database,sql:'SHOW TABLES'
        }})
        const d=res.data
        if(d.error){this.error=d.error;this.connected=false}else{this.tables=d.tables||[];this.connected=true}
      }catch(e){this.error=this.$t('tool.SqlQuery.errConnect')+': '+(e.message||'')}
      this.connecting=false
    },
    connectViaAgent(){
      this.stopHeartbeat()
      const protocol=location.protocol==='https:'?'wss:':'ws:'
      const url=protocol+'//'+location.host+'/ws/browser/'+this.conn.sessionId
      this.intentionalClose=false
      const old=this.ws; if(old){old.onclose=null;old.close()}
      this.ws=new WebSocket(url)
      this.ws.onopen=()=>{
        this.reconnectDelay=0
        this.agentConnected=true
        this.error=''
        this.startHeartbeat()
        this.ws.send(JSON.stringify({
          type:'connect',
          host:this.conn.host,
          port:Number(this.conn.port)||3306,
          user:this.conn.user,
          password:this.conn.password,
          database:this.conn.database
        }))
      }
      this.ws.onmessage=(e)=>{
        try{const msg=JSON.parse(e.data);this.handleAgentMsg(msg)}catch(ex){}
      }
      this.ws.onerror=()=>{
        if(!this.reconnectDelay){
          this.error=this.$t('tool.SqlQuery.errAgentConnect')
          this.connecting=false
        }
      }
      this.ws.onclose=()=>{
        this.agentConnected=false; this.loading=false
        if(!this.intentionalClose){
          this.scheduleReconnect()
        }else{
          this.connected=false;this.ws=null
        }
      }
    },
    handleAgentMsg(msg){
      switch(msg.type){
        case 'connected':
          this.tables=msg.tables||[]; this.connected=true
          this.connecting=false
          break
        case 'result':
          this.result=msg
          this.loading=false
          break
        case 'error':
          this.error=msg.message||'Unknown error'
          this.connecting=false; this.loading=false
          break
        case 'agentDisconnected':
          this.error=this.$t('tool.SqlQuery.agentDisconnected')
          break
        case 'waitingAgent':
          this.error=msg.message||'Agent disconnected, waiting...'
          break
        case 'agentReconnected':
          this.error=''
          this.ws.send(JSON.stringify({
            type:'connect',host:this.conn.host,
            port:Number(this.conn.port)||3306,
            user:this.conn.user,password:this.conn.password,
            database:this.conn.database
          }))
          break
        default:break
      }
    },
    selectTable(t){this.sql='SELECT * FROM '+t},
    async execute(){
      this.error='';this.result=null;if(!this.sql.trim())return;this.loading=true

      if(this.mode==='agent'&&this.ws&&this.ws.readyState===WebSocket.OPEN){
        this.ws.send(JSON.stringify({type:'query',sql:this.sql.trim()}))
        return
      }

      try{
        const res=await request({url:'/api/pub/sql/query',method:'post',data:{
          host:this.conn.host,port:Number(this.conn.port)||3306,
          user:this.conn.user,password:this.conn.password,
          database:this.conn.database,sql:this.sql.trim()
        }})
        const d=res.data
        if(d.error){this.error=d.error}else{this.result=d}
      }catch(e){this.error=this.$t('tool.SqlQuery.errQuery')+': '+(e.message||'')}
      this.loading=false
    },
    doAction(a){
      if(a==='clear'){this.sql='';this.result=null;this.error=''}
      if(a==='copy'){
        if(!this.result)return
        let text=''
        if(this.result.columns){
          text+=this.result.columns.join('\t')+'\n'
          this.result.values.forEach(r=>text+=r.join('\t')+'\n')
        }else{text=this.result.msg||''}
        navigator.clipboard.writeText(text);this.$message.success(this.$t("common.status.copied"))
      }
    }
  }
}
</script>

<style scoped>
.sq-tool { display:flex;flex-direction:column;gap:14px; }

/* Card */
.sq-card { background:#fff;border:1px solid #ebeef5;border-radius:12px;padding:18px 22px; }
.sq-card-err { background:#fff5f5;border-color:#fde0e0; }
.sq-card-hd { display:flex;align-items:center;gap:8px;margin-bottom:14px;font-size:13px;font-weight:700;color:#1a1f36;flex-wrap:wrap; }
.sq-card-hint { font-size:11px;color:#b0b8c4;margin-left:auto;font-weight:400; }
.sq-dot { width:8px;height:8px;border-radius:50%;flex-shrink:0; }
.sq-dot-blue { background:#4e7cff; }
.sq-dot-purple { background:#6b3cf0; }
.sq-dot-green { background:#43a047; }

/* Badge */
.sq-badge { font-size:10px;font-weight:600;padding:2px 10px;border-radius:10px; }
.sq-badge-on { background:#e8f8e8;color:#43a047;margin-left:auto; }

/* Mode switch */
.sq-mode-switch { margin-left:auto; }

/* Connection form */
.sq-form { display:flex;flex-direction:column;gap:10px; }
.sq-field { display:flex;flex-direction:column;gap:4px; }
.sq-field label { font-size:12px;font-weight:600;color:#909399; }
.sq-row { display:flex;gap:10px; }
.sq-row .sq-field { flex:1; }

/* Agent help */
.sq-agent-help { font-size:12px;color:#909399;background:#f0f9ff;border:1px solid #d0e8ff;border-radius:8px;padding:8px 12px;display:flex;align-items:center;gap:6px; }
.sq-agent-help i { color:#4e7cff; }
.sq-agent-dl { margin-left:auto;color:#4e7cff;font-weight:600;text-decoration:none; }
.sq-agent-dl:hover { text-decoration:underline; }

/* Table tags */
.sq-tags { display:flex;gap:6px;flex-wrap:wrap;margin-bottom:10px; }
.sq-tag { cursor:pointer;font-family:'SF Mono',Consolas,monospace;font-size:12px; }

/* SQL editor */
.sq-editor >>> textarea { font-family:'JetBrains Mono',Consolas,monospace!important;font-size:13px!important;border-radius:8px; }
.sq-actions { display:flex;gap:8px;margin-top:10px; }

/* Table */
.sq-table-wrap { overflow:auto;max-height:400px;border-radius:8px;border:1px solid #e4e7ed; }
.sq-table { width:100%;border-collapse:collapse;font-size:13px; }
.sq-table th { background:#f8f9fb;padding:8px 12px;text-align:left;font-weight:600;color:#606266;border-bottom:2px solid #e4e7ed;white-space:nowrap;position:sticky;top:0; }
.sq-table td { padding:7px 12px;border-bottom:1px solid #f0f2f5;color:#303133;max-width:300px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap; }
.sq-table tbody tr:hover { background:#f8f9fb; }

.sq-msg { font-size:14px;color:#67c23a;padding:8px 0; }
.sq-err { font-size:13px;color:#f56c6c; }
</style>
